import React from "react";
import { useNavigate } from "react-router-dom";

const Developers = () => {
  const navigate = useNavigate();

  const developerTools = [
    {
      icon: "⚡",
      title: "X1 SDK",
      description: "Complete JavaScript SDK for interacting with X EcoChain",
      features: ["TypeScript support", "React hooks", "ERC-20 & ERC-721"],
      status: "Live",
      link: "/docs/sdk"
    },
    {
      icon: "🔗",
      title: "Smart Contracts",
      description: "Deploy and manage smart contracts with our developer tools",
      features: ["Solidity support", "Hardhat plugins", "Truffle suite"],
      status: "Live",
      link: "/docs/contracts"
    },
    {
      icon: "🌐",
      title: "API Gateway",
      description: "REST and WebSocket APIs for real-time blockchain data",
      features: ["Real-time events", "Historical data", "Gas estimates"],
      status: "Live",
      link: "/docs/api"
    },
    {
      icon: "🛠️",
      title: "DevNet",
      description: "Test your applications on our development network",
      features: ["Free test tokens", "Instant mining", "Debug tools"],
      status: "Live",
      link: "/docs/devnet"
    },
    {
      icon: "📊",
      title: "Analytics Dashboard",
      description: "Monitor your dApp performance and user metrics",
      features: ["Real-time analytics", "User insights", "Gas tracking"],
      status: "Beta",
      link: "/docs/analytics"
    },
    {
      icon: "🔒",
      title: "Security Tools",
      description: "Audit and secure your smart contracts",
      features: ["Vulnerability scanner", "Gas optimizer", "Test suites"],
      status: "Coming Soon",
      link: "/docs/security"
    }
  ];

  const codeExample = `// Example: Deploy a smart contract on X EcoChain
import { X1SDK } from '@xecochain/sdk';

const sdk = new X1SDK({
  network: 'mainnet',
  apiKey: 'your-api-key'
});

// Deploy ERC-20 token
const contract = await sdk.deploy({
  name: 'GreenToken',
  symbol: 'GRN',
  initialSupply: '1000000'
});

console.log('Contract deployed:', contract.address);`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-eco-green hover:text-eco-blue mb-6 transition-colors"
          >
            ← Back to Ecosystem
          </button>
          <h1 className="text-4xl md:text-6xl font-black gradient-text mb-4">
            Developer Portal
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Everything you need to build, deploy, and scale sustainable Web3 applications on X EcoChain.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="glass-card p-6 rounded-2xl text-center border border-eco-green border-opacity-20">
            <div className="text-2xl font-black text-eco-green">5,000+</div>
            <div className="text-gray-400 text-sm">Developers</div>
          </div>
          <div className="glass-card p-6 rounded-2xl text-center border border-eco-blue border-opacity-20">
            <div className="text-2xl font-black text-eco-blue">200+</div>
            <div className="text-gray-400 text-sm">dApps Deployed</div>
          </div>
          <div className="glass-card p-6 rounded-2xl text-center border border-eco-purple border-opacity-20">
            <div className="text-2xl font-black text-eco-purple">99.9%</div>
            <div className="text-gray-400 text-sm">Uptime</div>
          </div>
          <div className="glass-card p-6 rounded-2xl text-center border border-eco-green border-opacity-20">
            <div className="text-2xl font-black text-eco-green">&lt;2s</div>
            <div className="text-gray-400 text-sm">Avg. Block Time</div>
          </div>
        </div>

        {/* Developer Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {developerTools.map((tool, index) => (
            <div
              key={index}
              className="glass-card p-6 rounded-2xl border border-eco-green border-opacity-20 hover:border-eco-green hover:border-opacity-40 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-eco-green to-eco-blue rounded-xl flex items-center justify-center text-eco-black text-xl">
                  {tool.icon}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  tool.status === 'Live' ? 'bg-eco-green text-eco-black' :
                  tool.status === 'Beta' ? 'bg-eco-blue text-eco-black' :
                  'bg-gray-600 text-gray-200'
                }`}>
                  {tool.status}
                </span>
              </div>

              <h3 className="text-xl font-black text-white mb-2">{tool.title}</h3>
              <p className="text-gray-400 mb-4 text-sm">{tool.description}</p>

              <ul className="space-y-2 mb-4">
                {tool.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-xs text-eco-blue">
                    <span className="w-1.5 h-1.5 bg-eco-blue rounded-full mr-2"></span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate(tool.link)}
                className="w-full py-2 bg-eco-dark border border-eco-green border-opacity-30 rounded-xl text-eco-green font-semibold hover:bg-eco-green hover:bg-opacity-10 transition-all duration-300"
              >
                Explore Tool
              </button>
            </div>
          ))}
        </div>

        {/* Code Example Section */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-black text-white mb-4">Get Started in 5 Minutes</h2>
              <p className="text-gray-300 mb-6">
                Start building on X EcoChain with our comprehensive SDK and developer tools. 
                Deploy your first smart contract in under 5 minutes.
              </p>
              <div className="space-y-4">
                <button className="w-full py-3 bg-eco-green text-eco-black font-bold rounded-xl hover:scale-105 transition-transform duration-300">
                  Install SDK
                </button>
                <button 
                  onClick={() => navigate("/documentation")}
                  className="w-full py-3 bg-eco-blue text-eco-black font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                >
                  View Full Documentation
                </button>
              </div>
            </div>
            <div className="bg-eco-dark rounded-2xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="text-white font-mono text-sm">example.js</div>
                <button className="text-eco-green text-sm hover:text-eco-blue transition-colors">
                  Copy
                </button>
              </div>
              <pre className="text-green-400 text-sm overflow-x-auto">
                {codeExample}
              </pre>
            </div>
          </div>
        </div>

        {/* Community Section */}
        <div className="text-center">
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-30 max-w-4xl mx-auto">
            <h3 className="text-2xl font-black gradient-text mb-4">
              Join Our Developer Community
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Connect with other developers, get help, and stay updated with the latest features and updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="px-6 py-3 bg-purple-600 text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                onClick={() => window.open('https://discord.gg/xecochain', '_blank')}
              >
                Join Discord
              </button>
              <button 
                className="px-6 py-3 bg-gray-800 text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                onClick={() => window.open('https://github.com/xecochain', '_blank')}
              >
                GitHub
              </button>
              <button 
                className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300"
                onClick={() => window.open('https://twitter.com/xecochain', '_blank')}
              >
                Twitter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Developers;
